import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { CalendarIcon } from "lucide-react";
import { sv } from "date-fns/locale";
import { STATUSES, isValidSEPhone, STORES, DEFAULT_STORE, customersApi } from "../lib/api";
import { formatDateDMY, isoToDate, dateToISO } from "../lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  customerName: "",
  phone: "",
  device: "",
  status: "Mottagen",
  date: todayISO(),
  technician: "",
  notes: "",
  store: DEFAULT_STORE,
};

export const JobModal = ({ open, onClose, onSave, initial, technicians = [], defaultTechnician = "" }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneMissing, setPhoneMissing] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [customerMatches, setCustomerMatches] = useState([]);
  const [customerSearchBusy, setCustomerSearchBusy] = useState(false);
  const snapshotRef = useRef(null);           // Clean form reference set on open
  const isEdit = !!initial;

  // Deep equality (shallow, since all fields are primitives)
  const isDirty = React.useMemo(() => {
    if (!open || !snapshotRef.current) return false;
    const snap = snapshotRef.current;
    const keys = new Set([...Object.keys(snap), ...Object.keys(form)]);
    for (const k of keys) {
      const a = (snap[k] ?? "") === "" ? "" : snap[k];
      const b = (form[k] ?? "") === "" ? "" : form[k];
      if (a !== b) return true;
    }
    return snap.__phoneMissing !== phoneMissing;
  }, [form, open, phoneMissing]);

  // Block route navigation while the modal has unsaved changes. We
  // register a global guard function on `window` that nav buttons (Logo,
  // Hem, Ärenden) consult BEFORE calling navigate(). The guard asks the
  // user; returns true to proceed, false to cancel. This avoids needing
  // react-router's data-router useBlocker (which this app doesn't use).
  useEffect(() => {
    if (!open) {
      // Only clear if WE registered the guard.
      if (window.__vfmFormGuard?.__owner === "jobmodal") {
        window.__vfmFormGuard = null;
      }
      return;
    }
    const guard = () => {
      if (!isDirty) return true;
      const ok = window.confirm(
        "Du har osparade ändringar i formuläret. Vill du kasta dem och lämna?"
      );
      if (ok) {
        snapshotRef.current = null;
        onClose();
      }
      return ok;
    };
    guard.__owner = "jobmodal";
    window.__vfmFormGuard = guard;
    // Also protect against tab/window close and reload.
    const beforeUnload = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      if (window.__vfmFormGuard?.__owner === "jobmodal") {
        window.__vfmFormGuard = null;
      }
    };
  }, [open, isDirty, onClose]);

  useEffect(() => {
    if (open) {
      // Default technician for NEW jobs: use the logged-in user's linked
      // technician name if it exists in the active technicians list. Fall
      // back to empty string (shows "Välj tekniker") — never auto-pick
      // the first entry.
      const preselectTech = (!isEdit && defaultTechnician &&
                             technicians.includes(defaultTechnician))
        ? defaultTechnician : "";
      const defaults = {
        ...EMPTY,
        date: todayISO(),
        technician: preselectTech,
      };
      const merged = initial ? { ...defaults, ...initial } : defaults;
      if (!merged.store) merged.store = DEFAULT_STORE;
      setForm(merged);
      setPhoneError("");
      // Phone-missing default behaviour:
      //   • New job → always OFF (phone is required by default)
      //   • Edit job → derive from whether the saved job has a phone
      const pmInit = isEdit ? !merged.phone : false;
      setPhoneMissing(pmInit);
      setCustomerMatches([]);
      // Capture a clean snapshot used to detect unsaved changes. We stash
      // phoneMissing into the snapshot under a sentinel key.
      snapshotRef.current = { ...merged, __phoneMissing: pmInit };
    } else {
      // Clear snapshot when modal closes so dirty check is only active while open.
      snapshotRef.current = null;
    }
  }, [open, initial, technicians, defaultTechnician, isEdit]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Debounced customer search when creating a NEW job
  useEffect(() => {
    if (!open || isEdit) return;
    const term = (form.customerName || "").trim() + " " + (form.phone || "").trim();
    if (term.trim().length < 2) { setCustomerMatches([]); return; }
    const t = setTimeout(async () => {
      setCustomerSearchBusy(true);
      try {
        const q = (form.customerName || "").trim() || (form.phone || "").trim();
        if (q.length >= 2) {
          const results = await customersApi.search(q);
          setCustomerMatches(results.filter((c) => c.customerId !== form.customerId));
        }
      } catch { /* ignore */ }
      finally { setCustomerSearchBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.customerName, form.phone, open, isEdit, form.customerId]);

  const pickExistingCustomer = (c) => {
    setForm((p) => ({ ...p, customerId: c.customerId,
                     customerName: c.customerName,
                     phone: c.phone || "" }));
    setCustomerMatches([]);
    setPhoneMissing(!c.phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim() || !form.device.trim()) return;
    // Technician is required for NEW jobs (must be an explicit pick).
    // When editing we don't force this since the technician field is
    // only meaningful at creation time; existing jobs keep their value.
    if (!isEdit && !form.technician) {
      setPhoneError("");
      return;
    }
    if (!phoneMissing) {
      if (!form.phone.trim() || !isValidSEPhone(form.phone)) {
        setPhoneError("Ogiltigt telefonnummer. Markera 'Telefonnummer saknas' om kunden inte uppgivit ett.");
        return;
      }
    }
    setPhoneError("");
    setSaving(true);
    try {
      const payload = { ...form };
      if (phoneMissing) payload.phone = "";
      // Pass the UI intent flag so the backend knows the empty phone is
      // deliberate. On edits the backend doesn't require this flag.
      if (!isEdit) payload.phoneMissing = phoneMissing;
      await onSave(payload);
      // Save succeeded — clear snapshot so the dirty-guard won't ask
      // for confirmation when the parent closes the modal.
      snapshotRef.current = null;
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === "string" && msg.toLowerCase().includes("telefon")) {
        setPhoneError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Wrap close actions (Avbryt / X / ESC / click-outside) to confirm when dirty.
  // We use a ref to guard against the re-entry that happens because
  // Radix's Dialog fires onOpenChange(false) when `open` goes false, which
  // would otherwise re-trigger requestClose and loop the confirm prompt.
  const closingRef = useRef(false);
  const requestClose = () => {
    if (closingRef.current) return true;
    if (isDirty) {
      const proceed = window.confirm(
        "Du har osparade ändringar i formuläret. Vill du kasta dem och stänga?"
      );
      if (!proceed) return false;
    }
    closingRef.current = true;
    snapshotRef.current = null;
    // Let the parent setModalOpen(false) propagate through Radix without
    // re-triggering the confirm. Reset the latch next tick.
    setTimeout(() => { closingRef.current = false; }, 0);
    onClose();
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent
        data-testid="job-modal"
        className="max-w-2xl rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-bold display-tight"
            style={{ fontFamily: "Manrope, sans-serif", color: "#0F172A" }}
          >
            {isEdit ? "Redigera ärende" : "Nytt ärende"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Fyll i uppgifterna om ärendet nedan.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="customerName">Kundnamn *</Label>
              <Input
                id="customerName"
                data-testid="input-customer-name"
                value={form.customerName}
                onChange={(e) => { set("customerName", e.target.value); set("customerId", null); }}
                placeholder="t.ex. Anna Lindberg"
                required
                autoComplete="off"
              />
              {!isEdit && customerMatches.length > 0 && !form.customerId && (
                <div data-testid="customer-matches"
                     className="border border-slate-200 rounded-lg mt-1 max-h-40 overflow-auto bg-white shadow-sm">
                  <div className="px-3 py-1.5 text-xs text-slate-500 bg-slate-50 border-b">
                    Möjliga befintliga kunder — välj uttryckligen för att koppla
                  </div>
                  {customerMatches.map((c) => (
                    <button key={c.customerId} type="button"
                            data-testid={`customer-match-${c.customerId}`}
                            onClick={() => pickExistingCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-[#E6F1FB] text-sm border-b last:border-b-0">
                      <div className="font-medium text-slate-800">{c.customerName}</div>
                      <div className="text-xs text-slate-500">
                        {c.phone || "— inget telefon —"} · {c.jobCount} tidigare ärende{c.jobCount === 1 ? "" : "n"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {form.customerId && !isEdit && (
                <p data-testid="customer-linked" className="text-xs text-green-700 mt-1">
                  ✓ Kopplad till befintlig kund.{" "}
                  <button type="button" onClick={() => set("customerId", null)}
                          className="underline text-slate-500 hover:text-red-600">
                    Skapa ny kund istället
                  </button>
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefonnummer {!phoneMissing && "*"}</Label>
              <Input
                id="phone"
                data-testid="input-phone"
                value={form.phone}
                disabled={phoneMissing}
                onChange={(e) => {
                  set("phone", e.target.value);
                  if (phoneError) setPhoneError("");
                }}
                placeholder={phoneMissing ? "— saknas —" : "Ange telefonnummer"}
                aria-invalid={!!phoneError}
                className={phoneError ? "border-red-400 focus-visible:ring-red-400" : ""}
                required={!phoneMissing}
              />
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none mt-1">
                <input type="checkbox" checked={phoneMissing} data-testid="checkbox-phone-missing"
                       onChange={(e) => {
                         setPhoneMissing(e.target.checked);
                         if (e.target.checked) { setPhoneError(""); set("phone", ""); }
                       }}
                       className="h-3.5 w-3.5 accent-[#3B9EDA]" />
                Telefonnummer saknas
              </label>
              {phoneError && (
                <p data-testid="phone-error" className="text-xs text-red-600 mt-1">{phoneError}</p>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="device">Enhet / Modell *</Label>
              <Input
                id="device"
                data-testid="input-device"
                value={form.device}
                onChange={(e) => set("device", e.target.value)}
                placeholder="iPhone 15 Pro"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => set("status", v)}
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Butik *</Label>
              <Select
                value={form.store}
                onValueChange={(v) => set("store", v)}
              >
                <SelectTrigger data-testid="select-store">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STORES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`store-option-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="date">Datum</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    id="date"
                    data-testid="input-date"
                    className="w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-[#3B9EDA]"
                  >
                    <span className={form.date ? "" : "text-slate-400"}>
                      {form.date ? formatDateDMY(form.date) : "DD/MM/ÅÅÅÅ"}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align="start"
                  data-testid="date-popover-content"
                >
                  <Calendar
                    mode="single"
                    locale={sv}
                    selected={isoToDate(form.date)}
                    onSelect={(d) => {
                      set("date", dateToISO(d));
                      setDateOpen(false);
                    }}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Tekniker{!isEdit ? " *" : ""}</Label>
              <Select
                value={form.technician || undefined}
                onValueChange={(v) => set("technician", v)}
              >
                <SelectTrigger data-testid="select-technician">
                  <SelectValue placeholder="Välj tekniker" />
                </SelectTrigger>
                <SelectContent>
                  {technicians.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      Inga tekniker tillagda
                    </SelectItem>
                  ) : (
                    technicians.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`tech-option-${t}`}>
                        {t}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="notes">Anteckningar</Label>
              <Textarea
                id="notes"
                data-testid="input-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Beskrivning av felet, åtgärder, m.m."
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              data-testid="modal-cancel-btn"
              type="button"
              variant="outline"
              className="press-feedback rounded-full px-5 min-h-[44px]"
              onClick={requestClose}
            >
              Avbryt
            </Button>
            <Button
              data-testid="modal-save-btn"
              type="submit"
              disabled={saving}
              className="press-feedback rounded-full px-6 text-white min-h-[44px]"
              style={{ backgroundColor: "#3B9EDA" }}
            >
              {saving ? "Sparar…" : isEdit ? "Spara ändringar" : "Lägg till"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobModal;
